<script>
  import AtomRadioButton from "@/components/atoms/AtomRadioButton.vue";
  import AtomButton from "@/components/atoms/AtomButton.vue";
  import userStatusHistoryService from "@/services/userStatusHistoryService";
  import MoleculeTagSelector from "@/components/molecules/MoleculeTagSelector.vue";

  export default {
    name: "ActiveUserModal",
    components: {
      AtomRadioButton,
      AtomButton,
      MoleculeTagSelector,
    },
    props: {
      isVisible: {
        type: Boolean,
        default: false,
      },
      user: {
        type: Object,
        default: () => ({}),
      },
      reset: {
        type: Number,
        default: 1,
      },
      inactivityReason: {
        type: String,
        default: "",
      },
    },
    data() {
      return {
        selectedState: "",
        selectedComment: "",
        tags: [],
        tagsSelected: [],
        stateOptions: [
          {
            id: "retornoLicencia",
            label: "Retorno de licencia",
            value: "retornoLicencia",
            description: "Modelo que regresa de un permiso formal (maternidad, salud, etc).",
            applicableToReasons: ["vacaciones", "incapacidad", "licencia"],
          },
          {
            id: "return",
            label: "Reconexión",
            value: "return",
            description: "La modelo retomó luego de su inactivación con motivo desaparecida.",
            applicableToReasons: ["endContract", "waiver", "deserter"],
          },
          {
            id: "Reintegro",
            label: "Reintegro",
            value: "reintegro",
            description: "Modelo que ingresa nuevamente a la empresa después del motivo de inactivación <strong>renuncia, finalización de alianza o deserta(luego de 3 meses inactiva)</strong>",
            applicableToReasons: ["endContract", "waiver", "deserter"],
          },
          {
            id: "reconexionPorInactividad",
            label: "Reconexión por inactividad",
            value: "reconexionPorInactividad",
            description: "Modelo que fue suspendida por el sistema tras 4 días sin inicio de sesión.",
            applicableToReasons: ["Inactivación automática."],
          },
          {
            id: "adminGerencia",
            label: "Reunión con área",
            value: "adminGerencia",
            description: "Indica el área con la que se realizó la reunión previa a la activación del usuario.",
            applicableToReasons: ["admin"],
          },
        ],
      };
    },
    watch: {
      reset() {
        this.resetModalState();
      },
    },
    computed: {
      userFullName() {
        return this.user.artisticName;
      },
      inactivityReasonResolved() {
        return this.user?.userStatusHistory?.reason || this.user?.userStatusHistory?.eventType || this.inactivityReason;
      },
      modalType() {
        const eventType = this.user?.userStatusHistory?.eventType;
        const reason = this.inactivityReasonResolved;
        if (eventType === "license") return "license";
        if (eventType === "disableAutomated") return "suspended";
        if (reason === "admin") return "admin";
        return "return";
      },
      modalTitle() {
        if (this.modalType === "license") return "Activación anticipada de licencia";
        if (this.modalType === "suspended") return "Reactivar perfil suspendido";
        return "Reactivar modelo";
      },
      confirmButtonText() {
        if (this.modalType === "license") return "Confirmar retorno";
        if (this.modalType === "suspended") return "Reactivar perfil";
        return "Confirmar";
      },
      bannerDate() {
        const history = this.user?.userStatusHistory;
        if (!history) return "";
        const date = this.modalType === "license" ? history.endDate : history.eventDate || history.startDate;
        if (!date) return "";
        try {
          return new Intl.DateTimeFormat("es-ES", { day: "numeric", month: "long" }).format(new Date(date));
        } catch {
          return "";
        }
      },
      bannerReasonText() {
        const reasonMap = {
          endContract: "finalización de alianza",
          waiver: "renuncia",
          deserter: "deserción",
          admin: "motivo administrativo",
          vacaciones: "vacaciones",
          incapacidad: "incapacidad",
          licencia: "licencia",
        };
        return reasonMap[this.inactivityReasonResolved] || this.inactivityReasonResolved;
      },
      bannerText() {
        const name = this.userFullName;
        const date = this.bannerDate;
        if (this.modalType === "license") {
          return `<strong>${name}</strong> se encuentra en licencia${date ? ` hasta el <strong>${date}</strong>` : ""}.`;
        }
        return `<strong>${name}</strong> se encuentra inactiva por ${this.bannerReasonText}${date ? ` desde el <strong>${date}</strong>` : ""}.`;
      },
      filteredStateOptions() {
        const reason = this.inactivityReasonResolved;
        if (!reason) return this.stateOptions;
        return this.stateOptions.filter((option) => option.applicableToReasons.includes(reason));
      },
      validateForm() {
        if (this.modalType === "license") return this.selectedComment.trim() !== "";
        if (this.modalType === "admin") return this.tagsSelected.length > 0 && this.selectedComment.trim() !== "";
        if (this.modalType === "suspended") return true;
        return this.selectedState !== "";
      },
    },
    methods: {
      initAutoState() {
        if (this.modalType === "license") {
          this.selectedState = "retornoLicencia";
        } else if (this.modalType === "suspended") {
          this.selectedState = "reconexionPorInactividad";
        } else if (this.modalType === "admin") {
          this.selectedState = "adminGerencia";
        } else {
          this.selectedState = "";
        }
      },

      closeModal() {
        this.resetModalState();
        this.$emit("closeModal");
      },

      resetModalState() {
        this.selectedComment = "";
        this.tagsSelected = [];
        this.initAutoState();
      },

      async saveChanges() {
        if (!this.selectedState) {
          console.warn("Please select a state");
          return;
        }

        try {
          const historyData = {
            reason: this.selectedState,
            userId: this.user._id,
            ...(this.selectedComment && { description: this.selectedComment }),
            tags: this.tagsSelected.length ? [...new Set(this.tagsSelected)] : undefined,
          };

          await this.createReasonDisabled(historyData);

          const userData = {
            _id: this.user._id,
            reason: this.selectedState,
            isEnable: true,
            ...(this.selectedComment && { description: this.selectedComment }),
          };

          this.$emit("save", userData);
        } catch (error) {
          console.error("Error saving user state:", error);
        }
      },

      async createReasonDisabled(data) {
        try {
          const dataToSend = {
            ...data,
            userId: this.user._id,
            eventType: "enable",
            eventDate: new Date(),
          };
          await userStatusHistoryService.sendUserStatusHistory(dataToSend);
        } catch (error) {
          console.error("Error creating reason disabled:", error);
          throw error;
        }
      },
      cancelChanges() {
        this.closeModal();
      },
      async getTags() {
        try {
          const { data } = await this.$axios.get(`/tags?categories=admin`);
          const { tags = [] } = data;
          this.tags = tags;
          return tags;
        } catch (error) {
          console.error("Error getting tags:", error);
          return [];
        }
      },
      setTagSelected(valTagId) {
        const tagIdx = this.tagsSelected.findIndex((tagId) => tagId === valTagId);
        if (tagIdx !== -1) {
          this.tagsSelected.splice(tagIdx, 1);
        } else {
          this.tagsSelected = [valTagId];
        }
      },
      setSelectedState(state) {
        this.selectedState = state.id;
      },
    },
    mounted() {
      this.initAutoState();
      if (this.modalType === "admin") {
        this.getTags();
      }
    },
  };
</script>

<template>
  <div v-if="isVisible" class="activeUserModal">
    <div class="activeUserModal__overlay" @click="closeModal"></div>
    <div class="activeUserModal__container" :class="{ 'activeUserModal__container--suspended': modalType === 'suspended' }">
      <div class="activeUserModal__header" :class="{ 'activeUserModal__header--centered': modalType === 'suspended' }">
        <p class="activeUserModal__title">{{ modalTitle }}</p>
        <button @click="closeModal" class="activeUserModal__closeButton" v-if="modalType !== 'suspended'">
          <iconic name="close" />
        </button>
      </div>

      <div class="activeUserModal__body">
        <div v-if="modalType !== 'suspended'" class="activeUserModal__banner" :class="{ 'activeUserModal__banner--license': modalType === 'license' }">
          <iconic :name="modalType === 'license' ? 'umbrella_icon' : 'danger_icon'" class="activeUserModal__bannerIcon" :class="{ 'activeUserModal__bannerIcon--license': modalType === 'license' }" />
          <p v-html="bannerText"></p>
        </div>

        <template v-if="modalType === 'license'">
          <p class="activeUserModal__subtitle">Indica el motivo por el cual se realizará la activación antes de la fecha prevista.</p>
          <textarea class="activeUserModal__textarea" placeholder="Describe brevemente qué cambió..." v-model="selectedComment" />
        </template>

        <template v-else-if="modalType === 'admin'">
          <p class="activeUserModal__subtitle">Indica el área con la que se realizó la reunión previa.</p>
          <MoleculeTagSelector :tags="tags" :selected-tags="tagsSelected" :canDeleteAndCreateTags="false" @tag-selected="setTagSelected" />
          <textarea class="activeUserModal__textarea" placeholder="Escribe el motivo de la activación" v-model="selectedComment" />
        </template>

        <template v-else-if="modalType === 'return'">
          <p class="activeUserModal__subtitle">Selecciona el tipo de regreso:</p>
          <div class="activeUserModal__radioGroup">
            <div
              v-for="option in filteredStateOptions"
              :key="option.id"
              :class="`activeUserModal__radioOption ${selectedState === option.id ? 'activeUserModal__radioOption--selected' : ''}`"
              @click="setSelectedState(option)">
              <AtomRadioButton :label="option.label" :value="option.value" name="stateGroup" :description="option.description" :checked="selectedState === option.id" />
            </div>
          </div>
        </template>

        <template v-else-if="modalType === 'suspended'">
          <div class="activeUserModal__suspendedBody">
            <p class="activeUserModal__suspendedName">
              <strong>{{ userFullName }}</strong> fue suspendida automáticamente por el sistema.
            </p>
            <p class="activeUserModal__suspendedText">Al continuar, recuperará el acceso a la plataforma.</p>
          </div>
        </template>

        <div class="activeUserModal__actions">
          <AtomButton variant="secondary" @click="cancelChanges">Cancelar</AtomButton>
          <AtomButton variant="primary" :disabled="!validateForm" @click="saveChanges">{{ confirmButtonText }}</AtomButton>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
  .activeUserModal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;

    &__overlay {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(0, 0, 0, 0.5);
    }

    &__container {
      position: relative;
      background: white;
      border-radius: 12px;
      width: 90%;
      max-width: 500px;
      max-height: 90vh;
      overflow-y: auto;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);

      &--suspended {
        max-width: 347px;
      }
    }

    &__header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 20px 20px 16px;
      &--centered {
        justify-content: center;
      }
    }

    &__title {
      margin: 0;
      font-size: 20px;
      font-weight: 600;
      font-family: $newFont_bold;
      color: #bd0909;
    }

    &__closeButton {
      background: none;
      border: none;
      cursor: pointer;
      padding: 5px;
      color: #666;

      &:hover {
        color: #333;
      }
    }

    &__body {
      padding: 0 20px 20px;
      display: flex;
      flex-direction: column;
      gap: 16px;
    }

    &__banner {
      display: flex;
      align-items: flex-start;
      gap: 10px;
      padding: 12px 14px;
      border: 1px solid rgba(189, 9, 9, 0.3);
      background: rgba(189, 9, 9, 0.05);
      border-radius: 8px;
      font-family: $newFont_regular;
      font-size: 14px;
      color: #bd0909;
      line-height: 1.5;
      &--license {
        border: 1px solid rgba(250, 176, 67, 0.3);
        background: rgba(250, 176, 67, 0.1);
        color: #3d3d3d;
      }
    }

    &__bannerIcon {
      font-size: 16px;
      color: #bd0909;
      flex-shrink: 0;
      margin-top: 4px;
      &--license {
        color: #e7a544;
      }
    }

    &__subtitle {
      font-family: $newFont_regular;
      font-size: 14px;
      color: #3d3d3d;
      margin: 0;
    }

    &__textarea {
      width: 100%;
      min-height: 90px;
      padding: 12px;
      border: 1px solid #d9d9d9;
      border-radius: 8px;
      font-family: $newFont_regular;
      font-size: 14px;
      color: #3d3d3d;
      resize: vertical;
      outline: none;
      box-sizing: border-box;

      &::placeholder {
        color: #bfbfbf;
      }

      &:focus {
        border-color: #bd0909;
      }
    }

    &__radioGroup {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    &__radioOption {
      display: flex;
      flex-direction: column;
      width: 100%;
      padding: 15px 20px;
      border-radius: 8px;
      border: 1px solid #f0f0f0;
      background-color: #fafafa;
      transition: all 0.2s ease-in;
      cursor: pointer;
      &:hover {
        background-color: #f8f8f8;
      }
      &--selected {
        border-color: #bd0909;
        background-color: #bd090905;
      }
    }
    &__suspendedBody {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 22px;
    }
    &__suspendedName {
      font-family: $newFont_regular;
      font-size: 16px;
      color: #3d3d3d;
      width: 100%;
      font-weight: 400;
      text-align: center;
      margin: 0;
    }
    &__suspendedText {
      font-family: $newFont_regular;
      font-size: 16px;
      color: #3d3d3d;
      width: 80%;
      text-align: center;
      font-weight: 500;
      margin: 0;
    }
    &__actions {
      display: flex;
      justify-content: flex-end;
      gap: 12px;
      margin-top: 4px;
    }
  }
</style>
